/**
 * Error Tracking Service
 * Comprehensive error monitoring, aggregation, and reporting
 *
 * Features:
 * - Multi-provider support (Sentry, custom endpoints, Supabase)
 * - Error fingerprinting and deduplication
 * - Stack trace parsing and source mapping
 * - Error grouping and aggregation
 * - User impact tracking
 * - Error replay with session context
 * - Rate limiting and sampling
 * - Breadcrumbs for error context
 * - Performance impact correlation
 */

import { logger } from './logger'
import { metrics } from './metrics'
import { createClient } from '@/lib/supabase/client'

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  FATAL = 'fatal'
}

/**
 * Error category types
 */
export enum ErrorCategory {
  JAVASCRIPT = 'javascript',
  REACT = 'react',
  NETWORK = 'network',
  API = 'api',
  DATABASE = 'database',
  AUTH = 'auth',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  RATE_LIMIT = 'rate_limit',
  TIMEOUT = 'timeout',
  RESOURCE = 'resource',
  MEMORY = 'memory',
  STORAGE = 'storage',
  EXTERNAL = 'external',
  UNKNOWN = 'unknown'
}

/**
 * Error status
 */
export enum ErrorStatus {
  NEW = 'new',
  SEEN = 'seen',
  INVESTIGATING = 'investigating',
  RESOLVED = 'resolved',
  IGNORED = 'ignored',
  REGRESSED = 'regressed'
}

/**
 * Breadcrumb types
 */
export enum BreadcrumbType {
  NAVIGATION = 'navigation',
  CLICK = 'click',
  INPUT = 'input',
  CONSOLE = 'console',
  XHR = 'xhr',
  FETCH = 'fetch',
  WEBSOCKET = 'websocket',
  ERROR = 'error',
  CUSTOM = 'custom'
}

/**
 * Error provider types
 */
export enum ErrorProvider {
  SENTRY = 'sentry',
  CUSTOM = 'custom',
  SUPABASE = 'supabase',
  CONSOLE = 'console'
}

/**
 * Sampling strategy
 */
export enum SamplingStrategy {
  ALL = 'all',
  RANDOM = 'random',
  RATE_BASED = 'rate_based',
  ERROR_TYPE = 'error_type',
  USER_BASED = 'user_based'
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Error context
 */
export interface ErrorContext {
  userId?: string
  sessionId?: string
  path?: string
  component?: string
  action?: string
  requestId?: string
  traceId?: string
  version?: string
  release?: string
  environment?: string
  tags?: Record<string, string>
  extra?: Record<string, any>
  [key: string]: any
}

/**
 * Stack frame
 */
export interface StackFrame {
  filename?: string
  function?: string
  lineno?: number
  colno?: number
  context?: string[]
  inApp?: boolean
  module?: string
}

/**
 * Parsed error
 */
export interface ParsedError {
  name: string
  message: string
  stack?: string
  frames: StackFrame[]
  cause?: ParsedError
  raw: Error
}

/**
 * Breadcrumb
 */
export interface Breadcrumb {
  type: BreadcrumbType
  category: string
  message: string
  data?: Record<string, any>
  level: ErrorSeverity
  timestamp: number
}

/**
 * Error fingerprint
 */
export interface ErrorFingerprint {
  hash: string
  components: string[]
  similarity: number
}

/**
 * Error group
 */
export interface ErrorGroup {
  id: string
  fingerprint: ErrorFingerprint
  firstSeen: number
  lastSeen: number
  count: number
  userCount: number
  status: ErrorStatus
  severity: ErrorSeverity
  category: ErrorCategory
  title: string
  culprit: string
  users: Set<string>
  events: ErrorEvent[]
  assignee?: string
  notes?: string
}

/**
 * Error event
 */
export interface ErrorEvent {
  id: string
  timestamp: number
  error: ParsedError
  context: ErrorContext
  breadcrumbs: Breadcrumb[]
  fingerprint: ErrorFingerprint
  severity: ErrorSeverity
  category: ErrorCategory
  tags: Record<string, string>
  user?: UserInfo
  device?: DeviceInfo
  browser?: BrowserInfo
  os?: OSInfo
  request?: RequestInfo
  performance?: PerformanceInfo
}

/**
 * User info
 */
export interface UserInfo {
  id?: string
  email?: string
  username?: string
  ipAddress?: string
  segment?: string
}

/**
 * Device info
 */
export interface DeviceInfo {
  type: 'desktop' | 'mobile' | 'tablet' | 'unknown'
  model?: string
  vendor?: string
  memory?: number
  cpuCores?: number
}

/**
 * Browser info
 */
export interface BrowserInfo {
  name: string
  version: string
  userAgent: string
  language: string
  cookiesEnabled: boolean
  doNotTrack: boolean
}

/**
 * OS info
 */
export interface OSInfo {
  name: string
  version: string
}

/**
 * Request info
 */
export interface RequestInfo {
  url: string
  method: string
  headers?: Record<string, string>
  query?: Record<string, string>
  body?: string
  responseStatus?: number
}

/**
 * Performance info
 */
export interface PerformanceInfo {
  memoryUsageMB?: number
  jsHeapSizeMB?: number
  domNodes?: number
  pageLoadTime?: number
  firstContentfulPaint?: number
  largestContentfulPaint?: number
  timeToInteractive?: number
}

/**
 * Error provider config
 */
export interface ProviderConfig {
  provider: ErrorProvider
  enabled: boolean
  dsn?: string
  endpoint?: string
  apiKey?: string
  projectId?: string
  environment?: string
  release?: string
  sampleRate?: number
  maxBreadcrumbs?: number
  attachStacktrace?: boolean
  beforeSend?: (event: ErrorEvent) => ErrorEvent | null
}

/**
 * Error tracking config
 */
export interface ErrorTrackingConfig {
  enabled: boolean
  providers: ProviderConfig[]

  // Sampling
  sampleRate: number
  samplingStrategy: SamplingStrategy

  // Breadcrumbs
  maxBreadcrumbs: number
  autoBreadcrumbs: boolean

  // Deduplication
  deduplicationWindowMs: number
  maxEventsPerGroup: number

  // Rate limiting
  maxEventsPerMinute: number
  maxEventsPerSession: number

  // Filtering
  ignoreErrors: (string | RegExp)[]
  ignoreUrls: (string | RegExp)[]
  denyUrls: (string | RegExp)[]

  // Context
  defaultTags: Record<string, string>
  environment: string
  release: string

  // Performance
  attachPerformanceData: boolean
  attachDeviceData: boolean
}

/**
 * Error tracking stats
 */
export interface ErrorTrackingStats {
  totalErrors: number
  errorsByCategory: Record<ErrorCategory, number>
  errorsBySeverity: Record<ErrorSeverity, number>
  errorGroups: number
  uniqueUsers: number
  eventsDropped: number
  eventsSampled: number
  lastError?: number
  uptime: number
}

/**
 * Error event listener
 */
export type ErrorEventListener = (event: ErrorEvent, group: ErrorGroup) => void

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

const DEFAULT_CONFIG: ErrorTrackingConfig = {
  enabled: true,
  providers: [],
  sampleRate: 1.0,
  samplingStrategy: SamplingStrategy.ALL,
  maxBreadcrumbs: 100,
  autoBreadcrumbs: true,
  deduplicationWindowMs: 60000,
  maxEventsPerGroup: 100,
  maxEventsPerMinute: 60,
  maxEventsPerSession: 100,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    /^Script error\.?$/,
    /^Loading chunk \d+ failed/
  ],
  ignoreUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i
  ],
  denyUrls: [],
  defaultTags: {
    service: 'linkbox-web'
  },
  environment: process.env.NODE_ENV || 'development',
  release: process.env.NEXT_PUBLIC_VERSION || 'unknown',
  attachPerformanceData: true,
  attachDeviceData: true
}

// ============================================================================
// STACK TRACE PARSER
// ============================================================================

class StackTraceParser {
  private static readonly CHROME_REGEX = /^\s*at (?:(.+?) \()?(?:(?:(.+?):(\d+):(\d+))|(?:(.+?):(\d+)))\)?$/
  private static readonly FIREFOX_REGEX = /^(.+?)@(.+?):(\d+):(\d+)$/
  private static readonly SAFARI_REGEX = /^(.+?)@(.+?):(\d+)$/

  static parse(error: Error): StackFrame[] {
    if (!error.stack) return []

    const lines = error.stack.split('\n')
    const frames: StackFrame[] = []

    for (const line of lines) {
      const frame = this.parseFrame(line.trim())
      if (frame) {
        frames.push(frame)
      }
    }

    return frames
  }

  private static parseFrame(line: string): StackFrame | null {
    // Try Chrome format
    let match = line.match(this.CHROME_REGEX)
    if (match) {
      return {
        function: match[1] || '<anonymous>',
        filename: match[2] || match[5],
        lineno: parseInt(match[3] || match[6], 10) || undefined,
        colno: parseInt(match[4], 10) || undefined,
        inApp: this.isInApp(match[2] || match[5])
      }
    }

    // Try Firefox format
    match = line.match(this.FIREFOX_REGEX)
    if (match) {
      return {
        function: match[1] || '<anonymous>',
        filename: match[2],
        lineno: parseInt(match[3], 10) || undefined,
        colno: parseInt(match[4], 10) || undefined,
        inApp: this.isInApp(match[2])
      }
    }

    // Try Safari format
    match = line.match(this.SAFARI_REGEX)
    if (match) {
      return {
        function: match[1] || '<anonymous>',
        filename: match[2],
        lineno: parseInt(match[3], 10) || undefined,
        inApp: this.isInApp(match[2])
      }
    }

    return null
  }

  private static isInApp(filename?: string): boolean {
    if (!filename) return false
    return !filename.includes('node_modules') &&
      !filename.includes('webpack') &&
      !filename.startsWith('http') &&
      !filename.includes('<anonymous>')
  }
}

// ============================================================================
// ERROR FINGERPRINTER
// ============================================================================

class ErrorFingerprinter {
  static fingerprint(error: ParsedError, context?: ErrorContext): ErrorFingerprint {
    const components: string[] = []

    // Add error type
    components.push(error.name)

    // Add message template (normalize dynamic parts)
    const normalizedMessage = this.normalizeMessage(error.message)
    components.push(normalizedMessage)

    // Add top frame if available
    const topFrame = error.frames.find(f => f.inApp) || error.frames[0]
    if (topFrame) {
      components.push(`${topFrame.filename}:${topFrame.function}`)
    }

    // Add component if available
    if (context?.component) {
      components.push(context.component)
    }

    // Generate hash
    const hash = this.hashComponents(components)

    return {
      hash,
      components,
      similarity: 1.0
    }
  }

  private static normalizeMessage(message: string): string {
    return message
      .replace(/\d+/g, '<num>') // Replace numbers
      .replace(/['"][^'"]+['"]/g, '<str>') // Replace strings
      .replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '<uuid>') // Replace UUIDs
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '<email>') // Replace emails
      .substring(0, 200)
  }

  private static hashComponents(components: string[]): string {
    const str = components.join('|')
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(16).padStart(8, '0')
  }
}

// ============================================================================
// BREADCRUMB MANAGER
// ============================================================================

class BreadcrumbManager {
  private breadcrumbs: Breadcrumb[] = []
  private maxBreadcrumbs: number

  constructor(maxBreadcrumbs: number = 100) {
    this.maxBreadcrumbs = maxBreadcrumbs
  }

  add(breadcrumb: Omit<Breadcrumb, 'timestamp'>): void {
    const crumb: Breadcrumb = {
      ...breadcrumb,
      timestamp: Date.now()
    }

    this.breadcrumbs.push(crumb)

    // Trim if over limit
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs = this.breadcrumbs.slice(-this.maxBreadcrumbs)
    }
  }

  addNavigation(from: string, to: string): void {
    this.add({
      type: BreadcrumbType.NAVIGATION,
      category: 'navigation',
      message: `Navigated from ${from} to ${to}`,
      data: { from, to },
      level: ErrorSeverity.INFO
    })
  }

  addClick(target: string, text?: string): void {
    this.add({
      type: BreadcrumbType.CLICK,
      category: 'ui',
      message: `Clicked on ${target}`,
      data: { target, text },
      level: ErrorSeverity.INFO
    })
  }

  addInput(target: string): void {
    this.add({
      type: BreadcrumbType.INPUT,
      category: 'ui',
      message: `Input in ${target}`,
      data: { target },
      level: ErrorSeverity.INFO
    })
  }

  addConsole(level: string, message: string, args?: any[]): void {
    this.add({
      type: BreadcrumbType.CONSOLE,
      category: 'console',
      message,
      data: { level, args },
      level: level === 'error' ? ErrorSeverity.ERROR :
        level === 'warn' ? ErrorSeverity.WARNING : ErrorSeverity.INFO
    })
  }

  addXHR(method: string, url: string, statusCode?: number, duration?: number): void {
    this.add({
      type: BreadcrumbType.XHR,
      category: 'xhr',
      message: `${method} ${url}`,
      data: { method, url, statusCode, duration },
      level: statusCode && statusCode >= 400 ? ErrorSeverity.ERROR : ErrorSeverity.INFO
    })
  }

  addFetch(method: string, url: string, statusCode?: number, duration?: number): void {
    this.add({
      type: BreadcrumbType.FETCH,
      category: 'fetch',
      message: `${method} ${url}`,
      data: { method, url, statusCode, duration },
      level: statusCode && statusCode >= 400 ? ErrorSeverity.ERROR : ErrorSeverity.INFO
    })
  }

  addWebSocket(action: 'open' | 'close' | 'error' | 'message', url: string): void {
    this.add({
      type: BreadcrumbType.WEBSOCKET,
      category: 'websocket',
      message: `WebSocket ${action}: ${url}`,
      data: { action, url },
      level: action === 'error' ? ErrorSeverity.ERROR : ErrorSeverity.INFO
    })
  }

  addError(error: Error): void {
    this.add({
      type: BreadcrumbType.ERROR,
      category: 'error',
      message: error.message,
      data: { name: error.name },
      level: ErrorSeverity.ERROR
    })
  }

  addCustom(category: string, message: string, data?: Record<string, any>): void {
    this.add({
      type: BreadcrumbType.CUSTOM,
      category,
      message,
      data,
      level: ErrorSeverity.INFO
    })
  }

  getAll(): Breadcrumb[] {
    return [...this.breadcrumbs]
  }

  clear(): void {
    this.breadcrumbs = []
  }

  getRecent(count: number): Breadcrumb[] {
    return this.breadcrumbs.slice(-count)
  }
}

// ============================================================================
// RATE LIMITER
// ============================================================================

class ErrorRateLimiter {
  private eventCounts: Map<string, number[]> = new Map()
  private maxEventsPerMinute: number
  private maxEventsPerSession: number
  private sessionEventCount: number = 0

  constructor(maxPerMinute: number = 60, maxPerSession: number = 100) {
    this.maxEventsPerMinute = maxPerMinute
    this.maxEventsPerSession = maxPerSession
  }

  shouldAllow(fingerprint: string): boolean {
    const now = Date.now()
    const windowStart = now - 60000

    // Check session limit
    if (this.sessionEventCount >= this.maxEventsPerSession) {
      return false
    }

    // Get timestamps for this fingerprint
    const timestamps = this.eventCounts.get(fingerprint) || []

    // Remove old timestamps
    const recentTimestamps = timestamps.filter(t => t > windowStart)

    // Check rate limit
    if (recentTimestamps.length >= this.maxEventsPerMinute) {
      return false
    }

    // Record new event
    recentTimestamps.push(now)
    this.eventCounts.set(fingerprint, recentTimestamps)
    this.sessionEventCount++

    return true
  }

  getRemainingQuota(): { perMinute: number; perSession: number } {
    return {
      perMinute: this.maxEventsPerMinute,
      perSession: this.maxEventsPerSession - this.sessionEventCount
    }
  }

  reset(): void {
    this.eventCounts.clear()
    this.sessionEventCount = 0
  }
}

// ============================================================================
// ERROR GROUP MANAGER
// ============================================================================

class ErrorGroupManager {
  private groups: Map<string, ErrorGroup> = new Map()
  private maxEventsPerGroup: number

  constructor(maxEventsPerGroup: number = 100) {
    this.maxEventsPerGroup = maxEventsPerGroup
  }

  addEvent(event: ErrorEvent): ErrorGroup {
    const groupId = event.fingerprint.hash
    let group = this.groups.get(groupId)

    if (!group) {
      group = {
        id: groupId,
        fingerprint: event.fingerprint,
        firstSeen: event.timestamp,
        lastSeen: event.timestamp,
        count: 0,
        userCount: 0,
        status: ErrorStatus.NEW,
        severity: event.severity,
        category: event.category,
        title: event.error.message.substring(0, 100),
        culprit: this.getCulprit(event),
        users: new Set(),
        events: []
      }
      this.groups.set(groupId, group)
    }

    // Update group
    group.lastSeen = event.timestamp
    group.count++

    // Track unique users
    if (event.user?.id && !group.users.has(event.user.id)) {
      group.users.add(event.user.id)
      group.userCount++
    }

    // Update severity if higher
    if (this.severityRank(event.severity) > this.severityRank(group.severity)) {
      group.severity = event.severity
    }

    // Handle regression
    if (group.status === ErrorStatus.RESOLVED) {
      group.status = ErrorStatus.REGRESSED
    }

    // Store event (limited)
    group.events.unshift(event)
    if (group.events.length > this.maxEventsPerGroup) {
      group.events = group.events.slice(0, this.maxEventsPerGroup)
    }

    return group
  }

  getGroup(id: string): ErrorGroup | undefined {
    return this.groups.get(id)
  }

  getAllGroups(): ErrorGroup[] {
    return Array.from(this.groups.values())
  }

  getGroupsByStatus(status: ErrorStatus): ErrorGroup[] {
    return this.getAllGroups().filter(g => g.status === status)
  }

  updateGroupStatus(id: string, status: ErrorStatus): void {
    const group = this.groups.get(id)
    if (group) {
      group.status = status
    }
  }

  getTopGroups(limit: number = 10): ErrorGroup[] {
    return this.getAllGroups()
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
  }

  getRecentGroups(limit: number = 10): ErrorGroup[] {
    return this.getAllGroups()
      .sort((a, b) => b.lastSeen - a.lastSeen)
      .slice(0, limit)
  }

  private getCulprit(event: ErrorEvent): string {
    const frame = event.error.frames.find(f => f.inApp) || event.error.frames[0]
    if (frame) {
      return `${frame.filename}:${frame.lineno} in ${frame.function}`
    }
    return event.context.component || 'unknown'
  }

  private severityRank(severity: ErrorSeverity): number {
    const ranks: Record<ErrorSeverity, number> = {
      [ErrorSeverity.DEBUG]: 0,
      [ErrorSeverity.INFO]: 1,
      [ErrorSeverity.WARNING]: 2,
      [ErrorSeverity.ERROR]: 3,
      [ErrorSeverity.FATAL]: 4
    }
    return ranks[severity]
  }

  getStats(): { total: number; byStatus: Record<ErrorStatus, number> } {
    const byStatus: Record<ErrorStatus, number> = {
      [ErrorStatus.NEW]: 0,
      [ErrorStatus.SEEN]: 0,
      [ErrorStatus.INVESTIGATING]: 0,
      [ErrorStatus.RESOLVED]: 0,
      [ErrorStatus.IGNORED]: 0,
      [ErrorStatus.REGRESSED]: 0
    }

    for (const group of this.groups.values()) {
      byStatus[group.status]++
    }

    return {
      total: this.groups.size,
      byStatus
    }
  }
}

// ============================================================================
// DEVICE/BROWSER DETECTOR
// ============================================================================

class EnvironmentDetector {
  static getDeviceInfo(): DeviceInfo {
    if (typeof window === 'undefined') {
      return { type: 'unknown' }
    }

    const ua = navigator.userAgent
    let type: DeviceInfo['type'] = 'desktop'

    if (/Mobi|Android/i.test(ua)) {
      type = 'mobile'
    } else if (/Tablet|iPad/i.test(ua)) {
      type = 'tablet'
    }

    return {
      type,
      memory: (navigator as any).deviceMemory,
      cpuCores: navigator.hardwareConcurrency
    }
  }

  static getBrowserInfo(): BrowserInfo {
    if (typeof window === 'undefined') {
      return {
        name: 'unknown',
        version: 'unknown',
        userAgent: '',
        language: '',
        cookiesEnabled: false,
        doNotTrack: false
      }
    }

    const ua = navigator.userAgent
    let name = 'unknown'
    let version = 'unknown'

    // Detect browser
    if (ua.includes('Firefox')) {
      name = 'Firefox'
      version = ua.match(/Firefox\/(\d+)/)?.[1] || 'unknown'
    } else if (ua.includes('Chrome')) {
      name = 'Chrome'
      version = ua.match(/Chrome\/(\d+)/)?.[1] || 'unknown'
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      name = 'Safari'
      version = ua.match(/Version\/(\d+)/)?.[1] || 'unknown'
    } else if (ua.includes('Edge')) {
      name = 'Edge'
      version = ua.match(/Edge\/(\d+)/)?.[1] || 'unknown'
    }

    return {
      name,
      version,
      userAgent: ua,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === '1'
    }
  }

  static getOSInfo(): OSInfo {
    if (typeof window === 'undefined') {
      return { name: 'unknown', version: 'unknown' }
    }

    const ua = navigator.userAgent
    let name = 'unknown'
    let version = 'unknown'

    if (ua.includes('Windows')) {
      name = 'Windows'
      version = ua.match(/Windows NT (\d+\.\d+)/)?.[1] || 'unknown'
    } else if (ua.includes('Mac OS')) {
      name = 'macOS'
      version = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || 'unknown'
    } else if (ua.includes('Linux')) {
      name = 'Linux'
    } else if (ua.includes('Android')) {
      name = 'Android'
      version = ua.match(/Android (\d+)/)?.[1] || 'unknown'
    } else if (ua.includes('iOS')) {
      name = 'iOS'
      version = ua.match(/OS (\d+)/)?.[1] || 'unknown'
    }

    return { name, version }
  }

  static getPerformanceInfo(): PerformanceInfo | undefined {
    if (typeof window === 'undefined' || !window.performance) {
      return undefined
    }

    const perf: PerformanceInfo = {}

    // Memory info (Chrome only)
    const memory = (performance as any).memory
    if (memory) {
      perf.memoryUsageMB = Math.round(memory.usedJSHeapSize / 1048576)
      perf.jsHeapSizeMB = Math.round(memory.totalJSHeapSize / 1048576)
    }

    // DOM nodes
    perf.domNodes = document.querySelectorAll('*').length

    // Navigation timing
    const timing = performance.timing
    if (timing.loadEventEnd > 0) {
      perf.pageLoadTime = timing.loadEventEnd - timing.navigationStart
    }

    // Web Vitals (if available)
    const entries = performance.getEntriesByType('paint')
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') {
        perf.firstContentfulPaint = Math.round(entry.startTime)
      }
    }

    return perf
  }
}

// ============================================================================
// ERROR PROVIDERS
// ============================================================================

/**
 * Base error provider
 */
abstract class ErrorProviderBase {
  protected config: ProviderConfig

  constructor(config: ProviderConfig) {
    this.config = config
  }

  abstract send(event: ErrorEvent): Promise<void>
  abstract isEnabled(): boolean
}

/**
 * Custom endpoint provider
 */
class CustomProvider extends ErrorProviderBase {
  async send(event: ErrorEvent): Promise<void> {
    if (!this.config.endpoint) return

    await fetch(this.config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
      },
      body: JSON.stringify({
        event: this.serializeEvent(event),
        timestamp: new Date().toISOString(),
        environment: this.config.environment,
        release: this.config.release
      })
    })
  }

  isEnabled(): boolean {
    return this.config.enabled && !!this.config.endpoint
  }

  private serializeEvent(event: ErrorEvent): any {
    return {
      id: event.id,
      timestamp: event.timestamp,
      error: {
        name: event.error.name,
        message: event.error.message,
        stack: event.error.stack,
        frames: event.error.frames
      },
      context: event.context,
      breadcrumbs: event.breadcrumbs.slice(-20),
      fingerprint: event.fingerprint,
      severity: event.severity,
      category: event.category,
      tags: event.tags,
      user: event.user,
      device: event.device,
      browser: event.browser,
      os: event.os,
      performance: event.performance
    }
  }
}

/**
 * Console provider (for development)
 */
class ConsoleProvider extends ErrorProviderBase {
  async send(event: ErrorEvent): Promise<void> {
    const style = 'color: white; background: #e74c3c; padding: 2px 6px; border-radius: 3px;'
    console.group(`%c${event.error.name}`, style)
    console.error('Message:', event.error.message)
    console.log('Context:', event.context)
    console.log('Breadcrumbs:', event.breadcrumbs.slice(-5))
    if (event.error.stack) {
      console.log('Stack:', event.error.stack)
    }
    console.groupEnd()
  }

  isEnabled(): boolean {
    return this.config.enabled
  }
}

/**
 * Supabase provider
 */
class SupabaseProvider extends ErrorProviderBase {
  async send(event: ErrorEvent): Promise<void> {
    try {
      // Only run in browser environment where we have the session via cookies
      if (typeof window === 'undefined') return

      const supabase = createClient()

      const { error } = await supabase.from('error_logs').insert({
        level: event.severity,
        message: event.error.message,
        stack: event.error.stack,
        category: event.category,
        context: event.context,
        breadcrumbs: event.breadcrumbs,
        user_id: event.user?.id,
        environment: this.config.environment,
        version: this.config.release,
        fingerprint_hash: event.fingerprint.hash,
        timestamp: new Date(event.timestamp).toISOString()
      })

      if (error) {
        console.error('Failed to send error to Supabase:', error)
      }
    } catch (err) {
      console.error('Supabase provider error:', err)
    }
  }

  isEnabled(): boolean {
    return this.config.enabled
  }
}

// ============================================================================
// MAIN ERROR TRACKING SERVICE
// ============================================================================

export class ErrorTrackingService {
  private static instance: ErrorTrackingService
  private config: ErrorTrackingConfig
  private breadcrumbs: BreadcrumbManager
  private rateLimiter: ErrorRateLimiter
  private groupManager: ErrorGroupManager
  private providers: ErrorProviderBase[] = []
  private listeners: Set<ErrorEventListener> = new Set()
  private sessionId: string
  private userId?: string
  private startTime: number
  private eventsDropped: number = 0
  private eventsSampled: number = 0
  private initialized: boolean = false

  private constructor(config: Partial<ErrorTrackingConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.breadcrumbs = new BreadcrumbManager(this.config.maxBreadcrumbs)
    this.rateLimiter = new ErrorRateLimiter(
      this.config.maxEventsPerMinute,
      this.config.maxEventsPerSession
    )
    this.groupManager = new ErrorGroupManager(this.config.maxEventsPerGroup)
    this.sessionId = this.generateId()
    this.startTime = Date.now()

    // Initialize providers
    this.initializeProviders()

    // Set up auto breadcrumbs
    if (this.config.autoBreadcrumbs && typeof window !== 'undefined') {
      this.setupAutoBreadcrumbs()
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<ErrorTrackingConfig>): ErrorTrackingService {
    if (!ErrorTrackingService.instance) {
      ErrorTrackingService.instance = new ErrorTrackingService(config)
    }
    return ErrorTrackingService.instance
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  private initializeProviders(): void {
    for (const providerConfig of this.config.providers) {
      let provider: ErrorProviderBase

      switch (providerConfig.provider) {
        case ErrorProvider.CUSTOM:
          provider = new CustomProvider(providerConfig)
          break
        case ErrorProvider.SUPABASE:
          provider = new SupabaseProvider(providerConfig)
          break
        case ErrorProvider.CONSOLE:
        default:
          provider = new ConsoleProvider(providerConfig)
          break
      }

      if (provider.isEnabled()) {
        this.providers.push(provider)
      }
    }

    // Add default console provider in development
    if (this.providers.length === 0 && process.env.NODE_ENV === 'development') {
      this.providers.push(new ConsoleProvider({
        provider: ErrorProvider.CONSOLE,
        enabled: true
      }))
    }
  }

  private setupAutoBreadcrumbs(): void {
    // Console breadcrumbs
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error
    }

    console.log = (...args) => {
      this.breadcrumbs.addConsole('log', args[0]?.toString() || '', args.slice(1))
      originalConsole.log.apply(console, args)
    }

    console.warn = (...args) => {
      this.breadcrumbs.addConsole('warn', args[0]?.toString() || '', args.slice(1))
      originalConsole.warn.apply(console, args)
    }

    console.error = (...args) => {
      // Skip common non-critical errors
      const firstArg = args[0]?.toString() || ''
      const isAbortError = firstArg.includes('aborted') ||
        firstArg.includes('AbortError') ||
        firstArg.includes('signal is aborted')

      // Only track and breadcrumb actual errors, not abort signals
      if (!isAbortError) {
        this.breadcrumbs.addConsole('error', firstArg, args.slice(1))
      }
      originalConsole.error.apply(console, args)
    }

    // Click breadcrumbs
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const selector = this.getSelector(target)
      this.breadcrumbs.addClick(selector, target.textContent?.substring(0, 50))
    }, { passive: true })

    // Fetch breadcrumbs — DISABLED
    // The window.fetch monkeypatch was removed because this file exports its own
    // `RequestInfo` interface that shadows the global type, causing type conflicts
    // and breaking all fetch calls in production (including Supabase auth).
    // TODO: Re-add fetch breadcrumbs using a PerformanceObserver approach instead.

    this.initialized = true
  }

  private getSelector(element: HTMLElement): string {
    if (element.id) return `#${element.id}`
    if (element.className) {
      // className can be SVGAnimatedString for SVG elements, so convert to string first
      let classNameStr = ''
      if (typeof element.className === 'string') {
        classNameStr = element.className
      } else if (typeof element.className === 'object' && 'baseVal' in element.className) {
        classNameStr = (element.className as SVGAnimatedString).baseVal || ''
      }
      const classes = classNameStr.split(' ').filter(c => c).slice(0, 2).join('.')
      if (classes) return `.${classes}`
    }
    return element.tagName?.toLowerCase() || 'unknown'
  }

  // ==========================================================================
  // CAPTURE METHODS
  // ==========================================================================

  /**
   * Capture exception
   */
  captureException(
    error: Error,
    context?: ErrorContext,
    severity: ErrorSeverity = ErrorSeverity.ERROR
  ): string | null {
    if (!this.config.enabled) return null

    // Check if error should be ignored
    if (this.shouldIgnoreError(error)) {
      this.eventsDropped++
      return null
    }

    // Parse error
    const parsed = this.parseError(error)

    // Generate fingerprint
    const fingerprint = ErrorFingerprinter.fingerprint(parsed, context)

    // Check rate limit
    if (!this.rateLimiter.shouldAllow(fingerprint.hash)) {
      this.eventsDropped++
      return null
    }

    // Apply sampling
    if (!this.shouldSample(fingerprint)) {
      this.eventsSampled++
      return null
    }

    // Determine category
    const category = this.categorizeError(error, context)

    // Create event
    const event = this.createEvent(parsed, context, fingerprint, severity, category)

    // Add to group
    const group = this.groupManager.addEvent(event)

    // Log and track
    logger.error('Exception captured', error, context)
    metrics.increment('errors.exception', 1, {
      errorType: error.name,
      category,
      component: context?.component || 'unknown'
    })

    // Send to providers
    this.sendToProviders(event)

    // Notify listeners
    this.notifyListeners(event, group)

    return event.id
  }

  /**
   * Capture message
   */
  captureMessage(
    message: string,
    severity: ErrorSeverity = ErrorSeverity.INFO,
    context?: ErrorContext
  ): string | null {
    if (!this.config.enabled) return null

    const error = new Error(message)
    error.name = 'Message'

    // Only track as error metric if severity is error or fatal
    if (severity === ErrorSeverity.ERROR || severity === ErrorSeverity.FATAL) {
      return this.captureException(error, context, severity)
    }

    // Log message
    if (severity === ErrorSeverity.WARNING) {
      logger.warn(message, context)
    } else {
      logger.info(message, context)
    }

    return null
  }

  /**
   * Handle React error
   */
  handleReactError(error: Error, errorInfo: { componentStack: string }): string | null {
    return this.captureException(error, {
      component: 'React',
      componentStack: errorInfo.componentStack
    }, ErrorSeverity.ERROR)
  }

  /**
   * Handle API error
   */
  handleAPIError(
    error: any,
    endpoint: string,
    method: string,
    statusCode?: number
  ): string | null {
    const err = error instanceof Error ? error : new Error(String(error))
    return this.captureException(err, {
      component: 'API',
      endpoint,
      method,
      statusCode
    }, statusCode && statusCode >= 500 ? ErrorSeverity.ERROR : ErrorSeverity.WARNING)
  }

  /**
   * Handle network error
   */
  handleNetworkError(url: string, error: Error): string | null {
    return this.captureException(error, {
      component: 'Network',
      url
    }, ErrorSeverity.WARNING)
  }

  /**
   * Handle validation error
   */
  handleValidationError(
    field: string,
    message: string,
    value?: any
  ): string | null {
    const error = new Error(`Validation failed for ${field}: ${message}`)
    error.name = 'ValidationError'
    return this.captureException(error, {
      component: 'Validation',
      field,
      value: value?.toString?.()?.substring(0, 100)
    }, ErrorSeverity.WARNING)
  }

  // ==========================================================================
  // CONTEXT METHODS
  // ==========================================================================

  /**
   * Set user context
   */
  setUser(user: UserInfo): void {
    this.userId = user.id
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    this.userId = undefined
  }

  /**
   * Add tag
   */
  addTag(key: string, value: string): void {
    this.config.defaultTags[key] = value
  }

  /**
   * Remove tag
   */
  removeTag(key: string): void {
    delete this.config.defaultTags[key]
  }

  /**
   * Add breadcrumb
   */
  addBreadcrumb(
    type: BreadcrumbType,
    category: string,
    message: string,
    data?: Record<string, any>,
    level: ErrorSeverity = ErrorSeverity.INFO
  ): void {
    this.breadcrumbs.add({ type, category, message, data, level })
  }

  /**
   * Get breadcrumbs
   */
  getBreadcrumbs(): Breadcrumb[] {
    return this.breadcrumbs.getAll()
  }

  /**
   * Clear breadcrumbs
   */
  clearBreadcrumbs(): void {
    this.breadcrumbs.clear()
  }

  // ==========================================================================
  // ERROR GROUPS
  // ==========================================================================

  /**
   * Get error group
   */
  getErrorGroup(id: string): ErrorGroup | undefined {
    return this.groupManager.getGroup(id)
  }

  /**
   * Get all error groups
   */
  getErrorGroups(): ErrorGroup[] {
    return this.groupManager.getAllGroups()
  }

  /**
   * Get top error groups
   */
  getTopErrors(limit: number = 10): ErrorGroup[] {
    return this.groupManager.getTopGroups(limit)
  }

  /**
   * Get recent error groups
   */
  getRecentErrors(limit: number = 10): ErrorGroup[] {
    return this.groupManager.getRecentGroups(limit)
  }

  /**
   * Update error group status
   */
  updateErrorStatus(groupId: string, status: ErrorStatus): void {
    this.groupManager.updateGroupStatus(groupId, status)
  }

  // ==========================================================================
  // EVENT LISTENERS
  // ==========================================================================

  /**
   * Add event listener
   */
  addEventListener(listener: ErrorEventListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // ==========================================================================
  // STATISTICS
  // ==========================================================================

  /**
   * Get stats
   */
  getStats(): ErrorTrackingStats {
    const groups = this.groupManager.getAllGroups()
    const errorsByCategory: Record<ErrorCategory, number> = {} as any
    const errorsBySeverity: Record<ErrorSeverity, number> = {} as any

    // Initialize
    Object.values(ErrorCategory).forEach(cat => errorsByCategory[cat] = 0)
    Object.values(ErrorSeverity).forEach(sev => errorsBySeverity[sev] = 0)

    let totalErrors = 0
    const uniqueUsers = new Set<string>()
    let lastError: number | undefined

    for (const group of groups) {
      totalErrors += group.count
      errorsByCategory[group.category] += group.count
      errorsBySeverity[group.severity] += group.count

      for (const user of group.users) {
        uniqueUsers.add(user)
      }

      if (!lastError || group.lastSeen > lastError) {
        lastError = group.lastSeen
      }
    }

    return {
      totalErrors,
      errorsByCategory,
      errorsBySeverity,
      errorGroups: groups.length,
      uniqueUsers: uniqueUsers.size,
      eventsDropped: this.eventsDropped,
      eventsSampled: this.eventsSampled,
      lastError,
      uptime: Date.now() - this.startTime
    }
  }

  // ==========================================================================
  // PRIVATE METHODS
  // ==========================================================================

  private parseError(error: Error): ParsedError {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      frames: StackTraceParser.parse(error),
      cause: error.cause instanceof Error ? this.parseError(error.cause) : undefined,
      raw: error
    }
  }

  private createEvent(
    error: ParsedError,
    context: ErrorContext | undefined,
    fingerprint: ErrorFingerprint,
    severity: ErrorSeverity,
    category: ErrorCategory
  ): ErrorEvent {
    return {
      id: this.generateId(),
      timestamp: Date.now(),
      error,
      context: {
        ...context,
        sessionId: this.sessionId,
        environment: this.config.environment,
        release: this.config.release
      },
      breadcrumbs: this.breadcrumbs.getAll(),
      fingerprint,
      severity,
      category,
      tags: { ...this.config.defaultTags, ...context?.tags },
      user: this.userId ? { id: this.userId } : undefined,
      device: this.config.attachDeviceData ? EnvironmentDetector.getDeviceInfo() : undefined,
      browser: this.config.attachDeviceData ? EnvironmentDetector.getBrowserInfo() : undefined,
      os: this.config.attachDeviceData ? EnvironmentDetector.getOSInfo() : undefined,
      performance: this.config.attachPerformanceData ? EnvironmentDetector.getPerformanceInfo() : undefined
    }
  }

  private categorizeError(error: Error, context?: ErrorContext): ErrorCategory {
    // Check context first
    if (context?.component) {
      const comp = context.component.toLowerCase()
      if (comp === 'react') return ErrorCategory.REACT
      if (comp === 'api') return ErrorCategory.API
      if (comp === 'network') return ErrorCategory.NETWORK
      if (comp === 'database' || comp === 'db') return ErrorCategory.DATABASE
      if (comp === 'auth') return ErrorCategory.AUTH
      if (comp === 'validation') return ErrorCategory.VALIDATION
    }

    // Check error name
    const name = error.name.toLowerCase()
    if (name.includes('network') || name.includes('fetch')) return ErrorCategory.NETWORK
    if (name.includes('timeout')) return ErrorCategory.TIMEOUT
    if (name.includes('auth')) return ErrorCategory.AUTH
    if (name.includes('validation')) return ErrorCategory.VALIDATION
    if (name.includes('permission')) return ErrorCategory.PERMISSION

    // Check message
    const message = error.message.toLowerCase()
    if (message.includes('network') || message.includes('failed to fetch')) return ErrorCategory.NETWORK
    if (message.includes('timeout')) return ErrorCategory.TIMEOUT
    if (message.includes('rate limit')) return ErrorCategory.RATE_LIMIT
    if (message.includes('memory')) return ErrorCategory.MEMORY
    if (message.includes('storage') || message.includes('quota')) return ErrorCategory.STORAGE

    return ErrorCategory.JAVASCRIPT
  }

  private shouldIgnoreError(error: Error): boolean {
    const message = error.message

    for (const pattern of this.config.ignoreErrors) {
      if (typeof pattern === 'string') {
        if (message.includes(pattern)) return true
      } else {
        if (pattern.test(message)) return true
      }
    }

    return false
  }

  private shouldSample(fingerprint: ErrorFingerprint): boolean {
    if (this.config.sampleRate >= 1) return true
    if (this.config.sampleRate <= 0) return false

    // Use fingerprint hash for consistent sampling
    const hashNum = parseInt(fingerprint.hash.substring(0, 8), 16)
    const threshold = this.config.sampleRate * 0xFFFFFFFF
    return hashNum < threshold
  }

  private async sendToProviders(event: ErrorEvent): Promise<void> {
    const sendPromises = this.providers.map(async provider => {
      try {
        await provider.send(event)
      } catch (err) {
        console.error('Failed to send to error provider:', err)
      }
    })

    await Promise.allSettled(sendPromises)
  }

  private notifyListeners(event: ErrorEvent, group: ErrorGroup): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, group)
      } catch (err) {
        console.error('Error listener error:', err)
      }
    })
  }

  private generateId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`
  }
}

// ============================================================================
// LEGACY EXPORTS (Backwards Compatibility)
// ============================================================================

/**
 * Legacy error tracker class
 */
class LegacyErrorTracker {
  private service: ErrorTrackingService

  constructor() {
    this.service = ErrorTrackingService.getInstance()
  }

  captureException(error: Error, context?: ErrorContext): void {
    this.service.captureException(error, context)
  }

  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: ErrorContext
  ): void {
    const severity = level === 'error' ? ErrorSeverity.ERROR :
      level === 'warning' ? ErrorSeverity.WARNING :
        ErrorSeverity.INFO
    this.service.captureMessage(message, severity, context)
  }

  handleReactError(error: Error, errorInfo: { componentStack: string }): void {
    this.service.handleReactError(error, errorInfo)
  }

  handleAPIError(error: any, endpoint: string, method: string): void {
    this.service.handleAPIError(error, endpoint, method)
  }
}

export const errorTracker = new LegacyErrorTracker()

/**
 * Get error tracking service instance
 */
export function getErrorTrackingService(config?: Partial<ErrorTrackingConfig>): ErrorTrackingService {
  return ErrorTrackingService.getInstance(config)
}

// ============================================================================
// GLOBAL ERROR HANDLERS
// ============================================================================

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const service = ErrorTrackingService.getInstance()
    service.captureException(event.error || new Error(event.message), {
      component: 'Global',
      path: window.location.pathname,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const service = ErrorTrackingService.getInstance()

    // Create a proper Error object
    let error: Error
    if (event.reason instanceof Error) {
      // Don't mutate the original error's name property (it may be read-only like DOMException)
      // Instead, create a wrapper error that captures the rejection context
      error = event.reason
    } else {
      error = new Error(String(event.reason))
    }

    service.captureException(error, {
      component: 'Promise',
      path: window.location.pathname,
      errorType: 'UnhandledPromiseRejection',
      originalErrorName: event.reason?.name || 'Unknown'
    })
  })
}
